import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    useMultiStyleConfig,
    useDisclosure,
    Modal,
    ModalContent,
    ModalOverlay,
    ModalBody,
    ModalHeader,
    HStack,
    Grid,
    // Skeleton,
} from '@chakra-ui/react'
import {CheckoutProvider} from '../../checkout/util/checkout-context'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import CheckoutSkeleton from '../../checkout/partials/checkout-skeleton'
import {FormattedMessage} from 'react-intl'
import {Box, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import CloseIcon from '../../../components/icons/action/CloseIcon'
import {
    useAccessToken,
    useShopperBasketsMutation,
    useShopperCustomersMutation,
} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import './../../../vendors/adyen/style/adyen.css'
import Feedback from '../../../components/feedback'
import AddCard from './partials/AddCard'
import StoredCard from './partials/StoredCard'
import {AdyenCheckoutProvider, useAdyenCheckout} from '../../../vendors/adyen'

const PaymentMethods = ({updateRender, removalMessage = '', setRemovalMessage}) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const styles = useMultiStyleConfig('Account')
    const {adyenPaymentMethods} = useAdyenCheckout()
    const {data: customer} = useCurrentCustomer()
    const updateCustomer = useShopperCustomersMutation('updateCustomer')

    const [storedMethods, setStoredMethods] = useState([])

    useEffect(() => {
        if (adyenPaymentMethods?.storedPaymentMethods) {
            setStoredMethods(adyenPaymentMethods.storedPaymentMethods)
        }
    }, [adyenPaymentMethods])

    const handleItemRemoved = () => {
        if (setRemovalMessage) {
            setRemovalMessage('Card has been successfully removed.')
            setTimeout(() => setRemovalMessage(''), 3000)
        }
    }

    return (
        <Box data-testid="payment-methods-container">
            <Text as="h1" tabIndex="0" {...styles.accountTitle} data-testid="payment-methods-title">
                <FormattedMessage defaultMessage="Payment methods" id="payment_methods.title" />
            </Text>

            {removalMessage && (
                <HStack {...styles.feedbackContainer}>
                    <Feedback
                        message={removalMessage}
                        variant="removeFeedbackWishlist"
                        status="success"
                        enableIcon={false}
                    />
                </HStack>
            )}
            <Text {...styles.paymentMethodsSubtitle} data-testid="payment-methods-subtitle">
                <FormattedMessage
                    defaultMessage="Your credit and debit cards"
                    id="payment_methods.subtitle"
                />
            </Text>

            {/* {!adyenPaymentMethods && <Skeleton {...styles.storedCardSkeleton} />} */}
            {storedMethods.length > 0 && (
                <Grid {...styles.storedCardsContainer}>
                    {storedMethods.map((child, i) => {
                        return (
                            <StoredCard
                                key={i}
                                card={child}
                                updateRender={updateRender}
                                updateCustomer={updateCustomer}
                                isDefault={child.id === customer?.c_preferredPaymentToken}
                                handleItemRemoved={handleItemRemoved}
                            />
                        )
                    })}
                </Grid>
            )}

            <Button
                title="Add new card"
                onClick={onOpen}
                variant={'secondary'}
                {...styles.addCardButton}
            >
                <FormattedMessage
                    defaultMessage="Add new card"
                    id="payment_methods.new_card_modal_button"
                />
            </Button>
            <Modal isOpen={isOpen} onClose={onClose} isCentered {...styles.modalContainer}>
                <ModalOverlay />
                <ModalContent {...styles.modalContentContainer}>
                    <ModalHeader {...styles.modalHeader}>
                        <HStack {...styles.modalHeaderContainer}>
                            <Text {...styles.modalHeaderLabel}>
                                <FormattedMessage
                                    defaultMessage="Add a new card"
                                    id="payment_methods.modal.title"
                                />
                            </Text>
                            <CloseIcon data-testid="close-modal-button" onClick={onClose} />
                        </HStack>
                    </ModalHeader>
                    <ModalBody {...styles.modalBodyContainer}>
                        <AddCard closeModal={onClose} updateRender={updateRender} />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    )
}

PaymentMethods.propTypes = {
    updateRender: PropTypes.func,
    removalMessage: PropTypes.string,
    setRemovalMessage: PropTypes.func,
}

const checkoutCustomizations = {
    paymentMethodsConfiguration: {
        klarna: {
            useKlarnaWidget: false,
        },
        klarna_account: {
            useKlarnaWidget: false,
        },
    },
}

const PaymentMethodsContainer = () => {
    const {getTokenWhenReady} = useAccessToken()
    const {data: customer} = useCurrentCustomer()
    const {data: basket, isLoading} = useCurrentBasket()
    const createBasket = useShopperBasketsMutation('createBasket')
    const navigate = useNavigation()
    const {locale, site} = useMultiSite()

    const [authToken, setAuthToken] = useState()
    const [updateRender, setUpdateRender] = useState(0)
    const [removalMessage, setRemovalMessage] = useState('')

    useEffect(() => {
        /* Understand if the basket is already created, if not create it */
        if (!isLoading && !basket)
            createBasket.mutateAsync({
                body: {},
            })
    }, [basket, isLoading])

    useEffect(() => {
        const getToken = async () => {
            const token = await getTokenWhenReady()
            setAuthToken(token)
        }

        getToken()
    }, [])

    if (customer.isRegistered && !customer?.c_adyenToken) {
        return
    }

    if (!authToken) {
        return <CheckoutSkeleton />
    }

    const updateRenderCallback = () => {
        setUpdateRender(updateRender + 1)
    }

    return (
        <AdyenCheckoutProvider
            authToken={authToken}
            key={updateRender}
            customerId={customer.customerId}
            isCustomerRegistered={customer.isRegistered}
            locale={locale}
            site={site}
            basket={basket}
            navigate={navigate}
            adyenConfig={checkoutCustomizations}
        >
            <CheckoutProvider>
                <PaymentMethods
                    updateRender={updateRenderCallback}
                    removalMessage={removalMessage}
                    setRemovalMessage={setRemovalMessage}
                />
            </CheckoutProvider>
        </AdyenCheckoutProvider>
    )
}

PaymentMethods.propTypes = {
    useShopperBasketsMutation: PropTypes.any,
}

PaymentMethodsContainer.propTypes = {
    children: PropTypes.any,
    useAccessToken: PropTypes.any,
    useCurrentCustomer: PropTypes.any,
    useShopperBasketsMutation: PropTypes.any,
    useMultiSite: PropTypes.any,
    adyenConfig: PropTypes.any,
}

export default PaymentMethodsContainer
